"use client"

import React from "react"
import type { SearchResult } from "@/schema/search"
import { Badge, Button } from "@ui/shad"
import { Search, X } from "lucide-react"

import { SearchDialog } from "./dialogs/search-dialog"

const DEFAULT_BACKEND_TARGET_TYPES = ["Household", "Business", "Contact", "User"] as const
type BackendTargetType = (typeof DEFAULT_BACKEND_TARGET_TYPES)[number]

const BACKEND_TO_SEARCH_TYPE_MAP: Record<BackendTargetType, SearchResult["type"][]> = {
  Household: ["household", "client", "prospect"],
  Business: ["business"],
  Contact: ["contact"],
  User: ["user"],
}

const BACKEND_TYPE_PLACEHOLDER_LABELS: Record<BackendTargetType, string> = {
  Household: "households",
  Business: "businesses",
  Contact: "contacts",
  User: "users",
}

const SEARCH_TYPE_TO_BACKEND_TYPE: Partial<Record<SearchResult["type"], BackendTargetType>> = {
  household: "Household",
  client: "Household",
  prospect: "Household",
  business: "Business",
  contact: "Contact",
  user: "User",
}

const normalizeBackendTargetType = (type: string | null | undefined): BackendTargetType | undefined => {
  return type ? DEFAULT_BACKEND_TARGET_TYPES.find((allowed) => allowed.toLowerCase() === type.toLowerCase()) : undefined
}

const allowedSearchTypesFor = (backendTypes: BackendTargetType[]): SearchResult["type"][] | undefined => {
  const searchTypes = backendTypes.flatMap((backendType) => BACKEND_TO_SEARCH_TYPE_MAP[backendType] ?? [])
  return searchTypes.length > 0 ? Array.from(new Set(searchTypes)) : undefined
}

const placeholderFor = (backendTypes: BackendTargetType[]): string => {
  if (backendTypes.length === 0) return "Search..."

  const labels = backendTypes.map((type) => BACKEND_TYPE_PLACEHOLDER_LABELS[type])
  if (labels.length === 1) return `Search for ${labels[0]}...`

  const [last, ...rest] = labels.slice().reverse()
  return `Search for ${rest.reverse().join(", ")} or ${last}...`
}

interface CrossReferenceSelectorProps {
  value?: {
    type: string
    id: string
    label?: string
  }
  onChange?: (value: { type: string; id: string; label: string } | null) => void
  placeholder?: string
  allowedTypes?: readonly string[]
  autoOpen?: boolean
  inline?: boolean // When true, only renders the dialog without the button
  open?: boolean // Controlled open state for inline mode
  onOpenChange?: (open: boolean) => void // Controlled open handler for inline mode
  disabled?: boolean
  bookIdOverride?: string
}

export function CrossReferenceSelector({
  value,
  onChange,
  placeholder = "Select reference...",
  allowedTypes = DEFAULT_BACKEND_TARGET_TYPES,
  autoOpen = false,
  inline = false,
  open,
  onOpenChange,
  disabled = false,
  bookIdOverride,
}: CrossReferenceSelectorProps) {
  const [internalDialogOpen, setInternalDialogOpen] = React.useState(autoOpen)
  const dialogOpen = inline && open !== undefined ? open : internalDialogOpen
  const setDialogOpen = inline && onOpenChange ? onOpenChange : setInternalDialogOpen
  const normalizedAllowedTypes = React.useMemo(() => {
    const configured = Array.isArray(allowedTypes) ? allowedTypes : [allowedTypes]
    const normalized = configured
      .map((type) => normalizeBackendTargetType(type))
      .filter((type): type is BackendTargetType => Boolean(type))

    return normalized.length > 0 ? Array.from(new Set(normalized)) : Array.from(DEFAULT_BACKEND_TARGET_TYPES)
  }, [allowedTypes])

  const allowedSearchTypes = React.useMemo(
    () => allowedSearchTypesFor(normalizedAllowedTypes),
    [normalizedAllowedTypes]
  )

  const dialogPlaceholder = React.useMemo(() => placeholderFor(normalizedAllowedTypes), [normalizedAllowedTypes])

  React.useEffect(() => {
    if (autoOpen && !value) {
      setDialogOpen(true)
    }
  }, [autoOpen, value, setDialogOpen])

  const handleSelect = React.useCallback(
    (item: SearchResult) => {
      const backendType = SEARCH_TYPE_TO_BACKEND_TYPE[item.type] ?? "Household"
      if (!normalizedAllowedTypes.includes(backendType)) return

      onChange?.({ type: backendType, id: item.id, label: item.display_label })
    },
    [onChange, normalizedAllowedTypes]
  )

  const handleClear = React.useCallback(() => {
    onChange?.(null)
  }, [onChange])

  // For inline mode, only render the dialog
  if (inline) {
    return (
      <SearchDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSelect={handleSelect}
        title="Select Reference"
        placeholder={dialogPlaceholder}
        allowedTypes={allowedSearchTypes}
        bookIdOverride={bookIdOverride}
        showPreview={true}
        widthVariant="compact"
      />
    )
  }

  // Default mode with button
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        className="flex-1 justify-start"
        onClick={() => setDialogOpen(true)}
        disabled={disabled}
      >
        {value ? (
          <div className="flex flex-1 items-center gap-2">
            <span className="truncate">{value.label || `${value.type} #${value.id}`}</span>
            <Badge variant="secondary" size="sm">
              {value.type}
            </Badge>
          </div>
        ) : (
          <div className="text-muted-foreground flex items-center gap-2">
            <Search className="h-4 w-4" />
            <span>{placeholder}</span>
          </div>
        )}
      </Button>

      {value && (
        <Button type="button" variant="ghost" size="icon" onClick={handleClear} disabled={disabled}>
          <X className="h-4 w-4" />
          <span className="sr-only">Clear selection</span>
        </Button>
      )}

      <SearchDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSelect={handleSelect}
        title="Select Reference"
        placeholder={dialogPlaceholder}
        allowedTypes={allowedSearchTypes}
        bookIdOverride={bookIdOverride}
        showPreview={true}
        widthVariant="compact"
      />
    </div>
  )
}
